/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {useIsPhone} from '../../hooks/useMedia';
import {LogoutIcon} from '../../icons/LogoutIcon';
import Render from '../../render/Render';
import {IconSize} from '../../types/types';
import {ButtonSpinner} from '../loading/ButtonSpinner';
import {IconButton} from './IconButton';
import {PrimaryButton} from './PrimaryButton';

/**
 * Defines the Prop Type definitions for the `LogoutButton` component.
 *
 * @property onLogout - Function which is invoked when the logout button is clicked.
 * @property isLoggingOut - Specifies if the logout process is currently in progress.
 * @property iconOnly - Indicates whether to display only the icon or a full button.
 *           Defaults to true, which means it will render an icon-only button. If set
 *           to false, it will render a full button with text.
 * @property label - Custom text to be displayed for the button. If not provided,
 *                 uses the localized 'Logout' text.
 */
export interface LogoutButtonProps {
  onLogout: () => void | Function;
  isLoggingOut: boolean;
  iconOnly?: boolean;
  label?: string
}

/**
 * The LogoutButton provides the standard DLS Log Out Button which can be used in
 * either Icon Only Mode, or to display a Full Width Logout Button which provides
 * Logout Progress indication to users.
 *
 * ```typescript
 *  import {LogoutButton} from '@ion/dls/src/components/buttons/LogoutButton';
 *  ...
 *
 *  <LogoutButton onLogout={logout} isLoggingOut={isLoggingOut} />
 *
 * ```
 */
export const LogoutButton = ({onLogout, isLoggingOut, label, iconOnly = true}: LogoutButtonProps) => {
  const isPhone = useIsPhone();
  if (!iconOnly) {
    return (
      <PrimaryButton fullWidth padding={'12px'} onClick={onLogout} disabled={isLoggingOut}>
        <Render>
          <Render.If condition={!isLoggingOut}>
            {label || translate('nav:header.logout')}
          </Render.If>
          <Render.Else>
            <ButtonSpinner />
          </Render.Else>
        </Render>
      </PrimaryButton>
    );
  }
  return (
    <IconButton icon={<LogoutIcon size={isPhone ? IconSize.medium : IconSize.large} />}
      style={{padding: '8px'}}
      onClick={onLogout}
      disabled={isLoggingOut} />
  );
};
export default React.memo(LogoutButton);
